import { useCallback, useRef, useState } from 'react';
import { WriteRefusedError } from '../../../lib/apiFetchJson';
import { toastUnlessRefused } from '../../../lib/errors';
import type { EnrollmentItem, StagedSendConfig } from '@shared/flow/enrollment';
import type { SendItemsOutcome } from './enroll-queue-types';
import { itemBlockReason } from './itemBlockReason';

/**
 * Glaze-free leaf module (flow-contacts-bulk-send plan, U4) — the pure
 * group-send logic the inline By Play card (`PlayQueueCard.tsx`) uses.
 *
 * Everything here was originally MOVED, not rewritten, out of the
 * now-retired full-screen review dialog's `ReviewGroup` (retired U4c, once
 * `PlayQueueCard` became the only review/send surface): the plan's "Reuse,
 * don't fork" rule meant the all-settled save-then-send pipeline and the
 * sendability derivation existed exactly once even while both surfaces were
 * live. It lives in a `.ts` leaf
 * with no `@clay-run/glaze` import on purpose — see
 * docs/glaze/glaze-usage-patterns-terra.md §5 ("keep testable logic in a
 * Glaze-free leaf module").
 */

/**
 * Behavior specs 3–5 (moved verbatim from the retired review dialog's
 * `reviewBlockLabel`): sendability, the group summary line, and every row's
 * "will skip — <reason>" label all derive from the SAME per-item block
 * label — `itemBlockReason` (client) overridden by the server's `SendSkip`
 * reason when the last send response reported one for this item (spec 5) —
 * never a second, ad-hoc re-derivation.
 */
function groupBlockLabel(
  item: EnrollmentItem,
  sendReadOnly: boolean,
  sendSkipLabelFor: (itemId: number) => string | null,
  effectiveSenderFor?: (item: EnrollmentItem) => string | null,
): string | null {
  // D12 point 4 — evaluate the EFFECTIVE sender, not the raw column: a row
  // whose own `sender` is null (a legacy item, or a role-null rep's seed-
  // nothing) is genuinely sendable once the play has a staged global sender,
  // because the server resolves that staged sender onto the row at claim
  // time (U2, off the POST's `staged` field). Applied by SPREADING it over the
  // item — the exact precedent `EnrollmentItemEditor`'s own send guard
  // already uses for its live draft (`itemBlockReason({ ...item, sender:
  // draft.sender }, sendGate)`) — so `itemBlockReason` keeps its two-argument
  // shape and stays the one client-visible eligibility check.
  const effective = effectiveSenderFor
    ? { ...item, sender: effectiveSenderFor(item) }
    : item;
  return sendSkipLabelFor(item.id) ?? itemBlockReason(effective, sendReadOnly);
}

/** "N of M sendable[ — X reason1, Y reason2]" (behavior specs 3–4; moved
 * verbatim from the retired review dialog's `summarizeSendability`). The reason
 * breakdown is omitted once nothing is blocked (fully sendable) OR nothing at
 * all is sendable (each row's own "will skip" label already carries that
 * detail; spec 4's example is the bare "0 of M sendable"). */
function summarizeSendability(labels: Array<string | null>): { sendableCount: number; summary: string } {
  const total = labels.length;
  let sendableCount = 0;
  const reasonCounts = new Map<string, number>();
  for (const label of labels) {
    if (label === null) { sendableCount++; continue; }
    reasonCounts.set(label, (reasonCounts.get(label) ?? 0) + 1);
  }
  const base = `${sendableCount} of ${total} sendable`;
  if (sendableCount === 0 || reasonCounts.size === 0) return { sendableCount, summary: base };
  const reasonParts = Array.from(reasonCounts.entries()).map(([label, count]) => `${count} ${label}`);
  return { sendableCount, summary: `${base} — ${reasonParts.join(', ')}` };
}

export interface GroupSendability {
  /** Per-item skip label, positionally aligned with the `items` argument. */
  blockLabels: Array<string | null>;
  /** The ids "Send N" submits — exactly the open items with no block label. */
  sendableIds: number[];
  sendableCount: number;
  /** "N of M sendable[ — …]" summary line. */
  summary: string;
}

/**
 * The one derivation of "which of this play's open items can actually be
 * sent right now", used by `PlayQueueCard`. Positional
 * alignment between `items` and `blockLabels` is part of the contract —
 * callers zip them by index when rendering per-row skip labels.
 */
export function deriveGroupSendability(
  items: EnrollmentItem[],
  sendReadOnly: boolean,
  sendSkipLabelFor: (itemId: number) => string | null,
  /**
   * D12 point 4 — "what will this item ACTUALLY send as", i.e. the play's
   * staged global sender for a row that follows it, else the row's own
   * `sender`. Optional: a caller with no staging concept (or a surface that
   * predates D12) gets the raw-column behavior unchanged. Server-side
   * eligibility is deliberately untouched — by POST time the column already
   * holds this value.
   */
  effectiveSenderFor?: (item: EnrollmentItem) => string | null,
): GroupSendability {
  const blockLabels = items.map(item => groupBlockLabel(item, sendReadOnly, sendSkipLabelFor, effectiveSenderFor));
  const sendableIds = items.filter((_, i) => blockLabels[i] === null).map(it => it.id);
  const { sendableCount, summary } = summarizeSendability(blockLabels);
  return { blockLabels, sendableIds, sendableCount, summary };
}

/**
 * Decision 7 (flow-contacts-bulk-send plan) — "Overridden" is not a new
 * derivation or a new column: the nullable per-item draft fields already
 * carry the semantic. Mirrors (deliberately does NOT reuse)
 * `StepOverrideEditor.tsx:194`'s `isOverridden` comparison, which answers the
 * same question for a gong_flow STEP rather than for the item's own copy.
 *
 * Note the comparison is against `null`, not against the play's base copy: an
 * item whose rep-typed draft happens to equal the current base copy is still
 * an explicit override, and must keep following its own value if the base
 * later changes.
 *
 * D12 widened what the BADGE means without widening this function: "this row
 * has its own config" is now `isItemOverridden(item) || isSenderTouched(item.id)`
 * (the latter backed by `useStagedSenderTouched`, not this facade), composed
 * at the call site (`overriddenAccessory`). This half stays pure and
 * server-derived; the
 * sender half cannot be, because a seeded `sender` is indistinguishable in
 * the DB from a hand-picked one (`seedSenderForPlay`'s own doc comment), so
 * the distinction lives in the rep's own browser-persisted `senderTouched`
 * set. One consequence, accepted and documented in the plan: on a different
 * device (or after clearing site data) a hand-picked sender still SENDS
 * correctly but loses its badge, and a newly staged global sender there will
 * re-follow that row.
 */
export function isItemOverridden(item: EnrollmentItem): boolean {
  return item.subject_draft !== null || item.body_draft !== null;
}

/**
 * The post-add confirmation banner's copy (plan "Deferred to
 * Implementation" → banner/toast copy strings; the orchestrator's Q3 default
 * fixes the STRUCTURE, wording is implementer's-choice within it and is on
 * the REVIEW-THESE list for Spencer's QA pass).
 *
 * Shipped strings (recorded verbatim in this unit's notes file):
 * - added > 1, skipped > 0: "3 contacts added to queue for Acme. 2 skipped — no email, or already queued."
 * - added > 1, skipped = 0: "3 contacts added to queue for Acme."
 * - added = 1, skipped = 0: "1 contact added to queue for Acme."
 * - added = 0, skipped > 0: "Nothing added to queue for Acme. 2 skipped — no email, or already queued."
 */
export function formatQueueLandingBanner(input: { playName: string; added: number; skipped: number }): string {
  const { playName, added, skipped } = input;
  const addedPart = added === 0
    ? `Nothing added to queue for ${playName}.`
    : `${added} ${added === 1 ? 'contact' : 'contacts'} added to queue for ${playName}.`;
  if (skipped === 0) return addedPart;
  return `${addedPart} ${skipped} skipped — no email, or already queued.`;
}

export interface UseSendGroupPipelineArgs {
  /**
   * terra-ohe.7 (F1/DBOS durable-send migration U6) — REPLACES the deleted
   * client-side send-time materialization function.
   * `EnrollQueueContext.buildStagedSnapshot` packages
   * whatever this identity has staged for `itemIds`' plays into the wire
   * shape `sendItems`'s `staged` argument expects (`StagedSendConfig`) — pure,
   * synchronous, no autosave dispatch, no PATCH, no rollback. Called at POST
   * time (after the save phase below), not before it: since U2 the server
   * resolves the staged trio itself when it claims each row, so there is
   * nothing left to materialize ahead of the send.
   */
  buildStagedSnapshot: (itemIds: number[]) => StagedSendConfig | undefined;
  /** `EnrollQueueContext.flushAutosave` — the item-level debounced PATCH. */
  flushAutosave: (itemId: number) => Promise<void>;
  /** `EnrollQueueContext.saveStepOverrides` — the gong_flow step-override save. */
  saveStepOverrides: (itemId: number) => Promise<void>;
  /**
   * `EnrollQueueContext.sendItems` — resolves with the ADMISSION RESULT since
   * D12 Codex round-2 fix #6, so this pipeline can tell a skipped item apart
   * from a sent one. terra-ohe.7: also takes the batch's staged wire snapshot
   * (`buildStagedSnapshot`'s output) as its second argument.
   */
  sendItems: (itemIds: number[], staged?: StagedSendConfig) => Promise<SendItemsOutcome>;
  /** `EnrollQueueContext.beginSendPipeline`/`endSendPipeline` (the per-item lock). */
  beginSendPipeline: (itemIds: number[]) => boolean;
  endSendPipeline: (itemIds: number[]) => void;
}

export interface SendGroupPipeline {
  /** "Send N" — save every item in the group, then send them. */
  sendGroup: (itemIds: number[]) => Promise<void>;
  /**
   * True from the synchronous top of `sendGroup` until its `finally`,
   * covering the SAVE phase that `isSending` (flipped only at POST time in
   * `sendMutation.onMutate`) does not. OR it into the `sending` value handed
   * to the group's rows so a group save locks every per-row control too.
   */
  pending: boolean;
}

/**
 * Round-3 (F3) group-send pipeline, MOVED verbatim out of the retired
 * full-screen review dialog's `handleSendGroup` so the inline By Play card
 * (`PlayQueueCard`) runs the identical mechanics instead of a second copy
 * (plan U4 Approach, "Reuse, don't fork"). Every comment below is the
 * original's — the reasoning is unchanged, only the home is.
 *
 * Basic save-and-send (plan 2026-08-19-004, D2) — "save everything for these
 * ids, then send them" is the whole contract: the item-level debounced PATCH
 * (`flushAutosave`) and then the gong_flow step-override save
 * (`saveStepOverrides`), both of which must land while the items are still
 * editable (`assertOwnedEditableItem`, `src/services/flow/step-drafts.ts:96-107`).
 *
 * The group "Send N" button bypasses `EnrollmentItemEditor` entirely, and its
 * rows are usually COLLAPSED (no editor mounted at all), so without the save
 * phase it would ship a rep's just-typed step copy stale.
 *
 * A rejected save aborts the send: nothing is sent, and the per-step "Failed
 * to save — retry" error `saveStepOverrides` records marks the offending
 * step. There is no inline slot to render into (the rows are usually
 * collapsed) and the button's `void sendGroup(...)` call site would turn the
 * rejection into an unhandled rejection, so it catches and re-emits the FIRST
 * rejection's `err.message` through the toast channel the send results
 * already use — no invented copy, no new button, and never a silent dead
 * click on "Send N".
 */
export function useSendGroupPipeline({
  buildStagedSnapshot,
  flushAutosave,
  saveStepOverrides,
  sendItems,
  beginSendPipeline,
  endSendPipeline,
}: UseSendGroupPipelineArgs): SendGroupPipeline {
  // Round-3 (F3) — the same ref+state pair as `EnrollmentItemEditor.
  // handleSend`'s. This pair is SURFACE-INSTANCE-local (a double-click belt
  // for this one button); the cross-surface guarantees live in the context's
  // per-item pipeline lock (`beginSendPipeline`/`isItemBusy`), which
  // `sendGroup` claims for every item below.
  const pendingRef = useRef(false);
  const [pending, setPending] = useState(false);

  const sendGroup = useCallback(async (itemIds: number[]) => {
    // Round-3 (F3) — synchronous re-entry guard: a double-click on "Send N"
    // must not launch a second concurrent save/send pipeline. Checked-and-set
    // before the first await; released in `finally` (on a save failure the
    // group unlocks with the toast below; on success `isSending` has already
    // taken over from POST time).
    if (pendingRef.current) return;
    // Findings 2/5 (adversarial follow-up) — the CONTEXT per-item pipeline
    // lock, claimed for EVERY item in the group before any async work.
    // Refuses (claiming nothing) when any of them is already mid-pipeline on
    // any surface — e.g. a per-row Send still in its save phase, which this
    // surface's own pending flag above knows nothing about. The `groupBusy`
    // disable on the button is this refusal's visible state.
    if (!beginSendPipeline(itemIds)) return;
    // Round-5 adversarial follow-up (P2): `try` starts IMMEDIATELY after a
    // successful claim, same reasoning as the per-row handler.
    try {
      pendingRef.current = true;
      setPending(true);
      // P1-GROUP (plan 2026-08-19-004, U3): ALL-SETTLED aggregation, never
      // fail-fast `Promise.all` — one item's fast save-failure must not
      // unlock the group (the `finally` below) while a sibling item's save is
      // still in flight, unobserved, where a second "Send N" click could race
      // it. `saveStepOverrides` is itself all-settled per step, so every
      // started write — every item, every step — has settled before this
      // either toasts or sends.
      const results = await Promise.allSettled(itemIds.map(async id => {
        await flushAutosave(id);
        await saveStepOverrides(id);
      }));
      const rejections = results.filter((r): r is PromiseRejectedResult => r.status === 'rejected');
      if (rejections.length > 0) {
        // A refusal `apiFetch` already announced must not shadow (or repeat
        // on top of) an ordinary failure from a sibling item.
        const shown = rejections.find(r => !(r.reason instanceof WriteRefusedError)) ?? rejections[0];
        toastUnlessRefused(shown.reason, 'Failed to save your latest edits');
        return;
      }
      // terra-ohe.7 (F1/DBOS durable-send migration U6) — flush -> save ->
      // POST is now the WHOLE pipeline. `buildStagedSnapshot` is a pure,
      // synchronous read of the rep's browser-persisted staging (no PATCH,
      // no autosave dispatch); the server resolves the staged trio itself
      // when it claims each row (U2), so there is nothing here to
      // compensate on a skip or a failure — no rollback closure exists.
      await sendItems(itemIds, buildStagedSnapshot(itemIds));
    } finally {
      // Context lock first and unconditionally — shared state other surfaces
      // gate on must never leak past this pipeline.
      endSendPipeline(itemIds);
      pendingRef.current = false;
      setPending(false);
    }
  }, [buildStagedSnapshot, flushAutosave, saveStepOverrides, sendItems, beginSendPipeline, endSendPipeline]);

  return { sendGroup, pending };
}
